/** Ответы API глоссария (сервер: server/src/routes/projects/glossary.ts) — проверяются схемой, как sheetModel. */
import { z } from 'zod';
import { formOriginSchema, formProvenanceShape, stressSourceSchema } from './stressProvenanceModel';
import type { FormDecision } from './stressIndexModel';

const context = z.object({ sentence: z.string(), page: z.number().int() }).nullable();
/** none — нет файлов «Текст», idle — ещё не разбирали, no_text — разобран, а текста нет (скан). Сервер: glossaryContract TextStatus. */
export const textStatusSchema = z.enum(['none', 'idle', 'running', 'ready', 'failed', 'no_text']);

/** Файл «Текст» для выбора в «Разобрать»; pages — из карточки файла, может быть неизвестно. */
export const textFileSchema = z.object({ file_id: z.string(), file_name: z.string(), pages: z.number().int().nullable() });

/** Этап разбора файла «Текст» (сервер: services/bookText/parseProgress.ts PARSE_STAGES); null — файл ждёт очереди */
export const parseStageSchema = z.enum(['download', 'read', 'vocabulary', 'save']);
export type ParseStage = z.infer<typeof parseStageSchema>;

/** Последний разбор текста книги: какие файлы, итог, ход по файлам; stale — появился новый текст или сменились правила. */
export const textRunSchema = z.object({
  file_ids: z.array(z.string()),
  status: z.enum(['running', 'ready', 'failed']),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  stale: z.boolean(),
  /** Единый процент разбора файла по всем этапам и этап (сервер: services/bookText/runProgress.ts) */
  progress: z.array(z.object({ file_id: z.string(), percent: z.number().int().min(0).max(100), stage: parseStageSchema.nullable() })),
});
export const startTextRunResponseSchema = z.object({ text_run: textRunSchema });
/** Событие project:bookTextProgress (сервер: realtime/events.ts) — единый процент разбора файла и этап. */
export const bookTextProgressSchema = z.object({
  projectId: z.string(),
  fileId: z.string(),
  percent: z.number().int().min(0).max(100),
  stage: parseStageSchema,
});

export const stressFormSchema = z.object({
  form_id: z.string(),
  form: z.string(),
  form_key: z.string(),
  /** У формы одно ударение или пока ни одного: двойственных форм нет */
  variants: z.array(z.string()).max(1),
  origin: formOriginSchema,
  status: z.enum(['approved', 'rejected']),
  count: z.number().int(),
  ...formProvenanceShape,
});

export const stressEntrySchema = z.object({
  entry_id: z.string(),
  scope: z.enum(['book', 'shared']),
  project_id: z.string().nullable(),
  kind: z.enum(['stress', 'pronunciation']),
  word: z.string(),
  word_key: z.string(),
  stressed: z.string().nullable(),
  pronunciation: z.string().nullable(),
  source: z.string(),
  created_by: z.string().nullable(),
  created_by_name: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  stress_source: stressSourceSchema,
  count: z.number().int(),
  forms: z.array(stressFormSchema),
});

/** Файл «Глоссарий» без применённой загрузки — строка «имя · Разобрать». */
export const glossaryFileSchema = z.object({ file_id: z.string(), file_name: z.string() });
/** Загрузка, к которой окно вернётся (идёт разбор или ждёт «Добавить»). */
export const activeImportSchema = z.object({
  import_id: z.string(),
  status: z.enum(['parsing', 'ready']),
});

export const glossaryDataSchema = z.object({
  text_status: textStatusSchema,
  text_files: z.array(textFileSchema),
  text_run: textRunSchema.nullable(),
  // Сервер отдаёт всегда; значения по умолчанию — для записанных до части 2 образцов ответа
  glossary_files: z.array(glossaryFileSchema).default([]),
  /** Все файлы «Глоссарий», и разобранные тоже: сверка со списком файлов проекта из project:updated */
  glossary_file_ids: z.array(z.string()).default([]),
  active_import: activeImportSchema.nullable().default(null),
  entries: z.array(stressEntrySchema),
  shared: z.array(stressEntrySchema),
});

const wordSuggestionSchema = z.object({
  kind: z.literal('word'),
  word_key: z.string(),
  word: z.string(),
  count: z.number().int(),
  forms: z.array(z.object({ form: z.string(), form_key: z.string(), count: z.number().int() })),
  context,
});
const formSuggestionSchema = z.object({
  kind: z.literal('form'),
  entry_id: z.string(),
  form: z.string(),
  form_key: z.string(),
  count: z.number().int(),
  variants: z.array(z.string()).max(1),
  origin: z.enum(['carried', 'manual', 'dictionary']),
  context,
});

export const suggestionsPageSchema = z.object({
  text_status: textStatusSchema,
  items: z.array(z.discriminatedUnion('kind', [wordSuggestionSchema, formSuggestionSchema])),
  next_cursor: z.string().nullable(),
  /** В выбранной части (section) */
  total: z.number().int(),
  /** Сколько встречаются один раз — для строки «Встречаются один раз · N» */
  once_total: z.number().int(),
});

export type StressScope = 'book' | 'shared';
export type TextStatus = z.infer<typeof textStatusSchema>;
export type TextFile = z.infer<typeof textFileSchema>;
export type TextRun = z.infer<typeof textRunSchema>;
export type GlossaryFile = z.infer<typeof glossaryFileSchema>;
export type ActiveImport = z.infer<typeof activeImportSchema>;
export type BookTextProgress = z.infer<typeof bookTextProgressSchema>;
export type StressForm = z.infer<typeof stressFormSchema>;
export type StressEntry = z.infer<typeof stressEntrySchema>;
export type GlossaryData = z.infer<typeof glossaryDataSchema>;
export type WordSuggestion = z.infer<typeof wordSuggestionSchema>;
export type FormSuggestion = z.infer<typeof formSuggestionSchema>;
export type Suggestion = WordSuggestion | FormSuggestion;
export type SuggestionsPage = z.infer<typeof suggestionsPageSchema>;
export type SuggestionSection = 'frequent' | 'once';

export type CreateEntryInput =
  | {
      kind: 'stress'; entry_id: string; scope: StressScope; word: string; stress: number[]; source: 'manual' | 'suggestion';
      /** Меню слова в просмотрщике: решения по формам; без поля формы сервер считает сам */
      forms?: FormDecision[];
      /** Меню слова: слово со страницы — форма записи, даже если его нет в словнике книги */
      seen?: string;
    }
  | { kind: 'pronunciation'; entry_id: string; scope: StressScope; word: string; pronunciation: string; source: 'manual' | 'suggestion' };
/** Решение по форме существующей записи: без variants — ударение формы не меняется (переносится за записью) */
export type EntryFormDecision = { form_key: string; variants?: string[]; status: 'approved' | 'rejected' };
/**
 * Правка записи (сервер: glossaryContract patchEntryBody). Колонка шлёт по одному полю; меню слова — всё сразу:
 * сервер пишет одной транзакцией. word — только вместе со stress (замена «е» ↔ «ё»).
 */
/** seen — слово со страницы, которое меню добавляет формой (см. CreateEntryInput) */
export type EntryPatch = { stress?: number[]; word?: string; pronunciation?: string; scope?: StressScope; forms?: EntryFormDecision[]; seen?: string };
